import { TrendingUp, BarChart3, AlertTriangle } from 'lucide-react'

const getOperatorColorHex = (color) => {
  const colors = {
    orange: '#FF7900',
    tmobile: '#E20074',
    plus: '#00A651',
    play: '#9333EA',
  }
  return colors[color] || '#6b7280'
}

const getBarColor = (pct) => {
  if (pct >= 100) return '#16a34a'
  if (pct >= 80) return '#2563eb'
  if (pct >= 60) return '#d97706'
  return '#dc2626'
}

const getBannerStyles = (type) => {
  switch (type) {
    case 'success':
      return {
        containerBg: '#E6F7F0',
        containerBorder: '#10B981',
        iconColor: '#10B981',
        Icon: TrendingUp
      }
    case 'info':
      return {
        containerBg: '#EFF6FF',
        containerBorder: '#60A5FA',
        iconColor: '#3B82F6',
        Icon: BarChart3
      }
    case 'warning':
      return {
        containerBg: '#FFFBEB',
        containerBorder: '#F59E0B',
        iconColor: '#F59E0B',
        Icon: BarChart3
      }
    case 'error':
      return {
        containerBg: '#FEF2F2',
        containerBorder: '#EF4444',
        iconColor: '#EF4444',
        Icon: AlertTriangle
      }
    default:
      return {
        containerBg: '#F9FAFB',
        containerBorder: '#D1D5DB',
        iconColor: '#6B7280',
        Icon: BarChart3
      }
  }
}

const ProgressBar = ({ label, pct }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-gray-500 w-14 shrink-0 font-medium">{label}</span>
    <div className="flex-1 h-4 bg-white/60 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{
          width: `${Math.min(pct, 100)}%`,
          backgroundColor: getBarColor(pct)
        }}
      />
    </div>
    <span
      className="text-lg font-extrabold w-16 text-right shrink-0"
      style={{ color: getBarColor(pct) }}
    >
      {pct.toFixed(1)}%
    </span>
  </div>
)

// Parse forecast numbers from detail string like "(prognoza: 73 / cel: 300~400 szt.)"
const parseForecastDetail = (detail) => {
  if (!detail) return null
  const match = detail.match(/prognoza:\s*(\d+)\s*\/\s*cel:\s*(\d+)(?:~(\d+))?\s*(szt\.|akt\.)/)
  if (!match) return null
  return {
    forecast: parseInt(match[1]),
    goalMin: parseInt(match[2]),
    goalTarget: match[3] ? parseInt(match[3]) : parseInt(match[2]),
    unit: match[4]
  }
}

const OperatorSummaryCard = ({ operator }) => {
  const perf = operator.performance
  const styles = getBannerStyles(perf.type)
  const Icon = styles.Icon
  const hasProgressBars = perf.forecastPct != null && perf.forecastPctTarget != null
  const forecastInfo = parseForecastDetail(perf.detail)

  // MTD sales data
  const mtdSales = operator.goalData?.actual_sales_mtd
  const unit = (operator.code === 'TMOBILE' || operator.code === 'P4') ? 'akt.' : 'szt.'

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: styles.containerBg,
        borderColor: styles.containerBorder
      }}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-full bg-white shrink-0">
          <Icon className="h-4 w-4" style={{ color: styles.iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-800">
            <span style={{ color: getOperatorColorHex(operator.color) }}>
              {operator.name}
            </span>
          </p>
          <p className="text-xs text-gray-600">{perf.text}</p>
        </div>
        {/* Big forecast number — hero element */}
        {forecastInfo && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Prognoza</div>
            <div className="text-2xl font-black leading-none" style={{ color: styles.iconColor }}>
              {forecastInfo.forecast}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              / {forecastInfo.goalMin}{forecastInfo.goalTarget !== forecastInfo.goalMin ? `~${forecastInfo.goalTarget}` : ''} {forecastInfo.unit}
            </div>
          </div>
        )}
      </div>

      {/* Sprzedaż MTD — prominent display */}
      {mtdSales != null && (
        <div className="py-2 px-3 mb-2 rounded-md bg-white/70">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-gray-600">Sprzedaż MTD</span>
            <div className="flex items-baseline gap-1">
              <span
                className="text-xl font-black"
                style={{ color: getOperatorColorHex(operator.color) }}
              >
                {Math.round(mtdSales)}
              </span>
              <span className="text-xs text-gray-500 font-medium">{unit}</span>
            </div>
          </div>
          {operator.goalData?.sales_breakdown && Object.keys(operator.goalData.sales_breakdown).length > 0 && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-200/60 space-y-0.5">
              {Object.entries(operator.goalData.sales_breakdown).map(([name, data]) => {
                const mtd = typeof data === 'object' ? (data.mtd ?? 0) : data
                const goal = typeof data === 'object' ? data.goal : null
                const margin = typeof data === 'object' ? data.margin : null
                return (
                  <div key={name} className="flex justify-between text-xs text-gray-500">
                    <span>{name}</span>
                    <span className="font-semibold text-gray-700">
                      {Math.round(mtd)} szt.
                      {goal != null && <span className="font-normal text-gray-400 ml-1">/ {Math.round(goal)}</span>}
                      {margin != null && <span className="font-normal text-green-600 ml-1">({margin.toLocaleString('pl-PL')} PLN)</span>}
                    </span>
                  </div>
                )
              })}
              {(() => {
                const entries = Object.values(operator.goalData.sales_breakdown)
                const totalMargin = entries.reduce((sum, d) => sum + (typeof d === 'object' && d.margin ? d.margin : 0), 0)
                if (totalMargin <= 0) return null
                return (
                  <div className="flex justify-between text-xs font-semibold text-green-700 pt-0.5 border-t border-gray-200/40">
                    <span>Marża łączna</span>
                    <span>{totalMargin.toLocaleString('pl-PL')} PLN</span>
                  </div>
                )
              })()}
            </div>
          )}
        </div>
      )}

      {hasProgressBars ? (
        <div className="space-y-2.5 mt-3">
          <ProgressBar label="Minimum" pct={perf.forecastPct} />
          <ProgressBar label="Target" pct={perf.forecastPctTarget} />
        </div>
      ) : (
        <p className="text-sm font-bold mt-2" style={{ color: styles.iconColor }}>
          {perf.value}
          {perf.detail && (
            <span className="font-medium text-gray-600 ml-1">{perf.detail}</span>
          )}
        </p>
      )}
    </div>
  )
}

const OperatorSummaryBanner = ({ operators }) => {
  if (!operators || operators.length === 0) {
    return null
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {operators.map((operator) => (
        <OperatorSummaryCard key={operator.code} operator={operator} />
      ))}
    </div>
  )
}

export default OperatorSummaryBanner
